import { Component, HostListener } from '@angular/core';
import { DateAdapter, MAT_DATE_FORMATS } from '@angular/material/core';
import { NavigationEnd, NavigationStart, Router } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { connectState } from '@y42/angular/util';
import { AppQuery } from '@y42/app-state';
import { AuthService } from '@y42/auth';
import { MAT_DATE_FNS_FORMATS, Y42DateAdapter } from '@y42/date-time';
import { IS_PROD_BUILD, environment } from '@y42/environment';
import { GitQuery } from '@y42/git';
import { PushNotificationService } from '@y42/push-notification';
import { UserTrackingLegacyService } from '@y42/user-tracking-legacy';
import { combineLatest, fromEvent, of } from 'rxjs';
import {
  debounceTime,
  delay,
  distinctUntilChanged,
  filter,
  map,
  startWith,
  switchMap,
  tap,
  withLatestFrom,
} from 'rxjs/operators';

@UntilDestroy()
@Component({
  selector: 'y42-app',
  templateUrl: './app.component.html',
  providers: [
    { provide: DateAdapter, useClass: Y42DateAdapter },
    { provide: MAT_DATE_FORMATS, useValue: MAT_DATE_FNS_FORMATS },
  ],
})
export class AppComponent {
  constructor(
    private readonly router: Router,
    private readonly userTracking: UserTrackingLegacyService,
    private readonly authService: AuthService,
    private readonly appQuery: AppQuery,
    private readonly gitQuery: GitQuery,
    private readonly pushNotificationService: PushNotificationService,
  ) {
    this.setDocumentViewHeightOnWindowResize().pipe(untilDestroyed(this)).subscribe();
    this.restoreYScrollPositionOnNavigationEnd().pipe(untilDestroyed(this)).subscribe();
    this.reloadPageOnAppUpdate().pipe(untilDestroyed(this)).subscribe();
    if (IS_PROD_BUILD) {
      // do not show warning during local development
      this.checkGitStateOnWindowBeforeUnload().pipe(untilDestroyed(this)).subscribe();
    }
  }

  readonly state = connectState(this, {
    isLoading: combineLatest([this.appQuery.selectLoading(), this.gitQuery.isOperationOngoing()]).pipe(
      map((loadings) => loadings.some((loading) => loading)),
      distinctUntilChanged(),
      switchMap((loading) => (loading ? of(true) : of(false).pipe(delay(500)))),
    ),
  });

  @HostListener('window:beforeunload', ['$event'])
  async beforeunloadHandler() {
    await this.authService.stopTrackingSession();
  }

  private checkGitStateOnWindowBeforeUnload() {
    return fromEvent<BeforeUnloadEvent>(window, 'beforeunload').pipe(
      withLatestFrom(this.gitQuery.hasModifiedFiles$),
      tap(([event, shouldWarn]) => {
        if (shouldWarn) {
          event.preventDefault();
          event.returnValue = `You have uncommitted changes. They may not be saved`;
        }
      }),
    );
  }

  private setDocumentViewHeightOnWindowResize() {
    return fromEvent(window, 'resize').pipe(
      debounceTime(200),
      startWith(null),
      tap(() =>
        // CSS for 100vh on mobile
        document.documentElement.style.setProperty('--vh', `${window.innerHeight * 0.01}px`),
      ),
    );
  }

  private restoreYScrollPositionOnNavigationEnd() {
    return this.router.events.pipe(
      tap((ev) => {
        if (ev instanceof NavigationEnd) {
          this.userTracking.trackPage({ name: 'Y42', url: window.location.href });
        }
      }),
    );
  }

  private reloadPageOnAppUpdate() {
    return this.pushNotificationService
      .openChannel('releaseUpgrade.survey-analytics', { skipSetPrefix: true })
      .listen<{ version: string; env: string; project: string }>('versionChange')
      .pipe(
        filter((data) => data.env === environment.name),
        map((data) => data.version),
        tap((version) => console.debug(`[Y42]: new app release`, version)),
        switchMap((version) =>
          this.router.events.pipe(
            filter((evt) => evt instanceof NavigationStart),
            map((evt) => evt as NavigationStart),
            tap((evt) => {
              if (environment.release_version !== 'local' && version !== environment.release_version) {
                window.location.href = evt.url;
                return;
              }
            }),
          ),
        ),
      );
  }
}
